import { Track } from "../analytics";
import { FULLY_AUTHENTICATED, TRACKING_CONTEXT } from "../constants";

/**
 * Tracks a page view event.
 * @param {Track} track - tracking object.
 * @param {string} pageName - name of the page being viewed.
 * @param {string} customerNumber - customer number.
 */
export const trackView = (
  track: Track,
  pageName: string,
  customerNumber: string
) => {
  track.view({
    name: `${TRACKING_CONTEXT.PAGE.PREFIX}${pageName}`,
    cis_number: customerNumber,
    authentication_status: FULLY_AUTHENTICATED,
  });
};

/**
 * Tracks an error event.
 * @param {Track} track - tracking object.
 * @param {string} errorMessage - error message to log.
 */
export const trackError = (track: Track, errorMessage: string) => {
  track.view({
    name: `${TRACKING_CONTEXT.PAGE.PREFIX}${TRACKING_CONTEXT.PAGE.ERROR}`,
  });
  track.setDynamicMessage({
    cause: errorMessage,
    text: TRACKING_CONTEXT.DYNAMIC_MESSAGE.ERROR,
  });
};

/**
 * Tracks a process event with specific blockStatus, accountType, and investorType values.
 * @param {Track} track - tracking object.
 * @param {string} blockStatusValue - block status value.
 * @param {string} accountTypeValue - account type value.
 * @param {string} investorTypeValue - investor type value.
 */
export const trackProcess = (
  track: Track,
  blockStatusValue: string,
  accountTypeValue: string,
  investorTypeValue: string
) => {
  track.process([
    {
      category: TRACKING_CONTEXT.GAMBLING_CONTROLS,
      identifier: TRACKING_CONTEXT.BLOCK_STATUS.IDENTIFIER,
      value: blockStatusValue,
    },
    {
      category: TRACKING_CONTEXT.GAMBLING_CONTROLS,
      identifier: TRACKING_CONTEXT.ACCOUNT_TYPE.IDENTIFIER,
      value: accountTypeValue,
    },
    {
      category: TRACKING_CONTEXT.GAMBLING_CONTROLS,
      identifier: TRACKING_CONTEXT.INVESTOR_TYPE.IDENTIFIER,
      value: investorTypeValue,
    },
  ]);
};

/**
 * Tracks an external link click event.
 * @param {Track} track - tracking object.
 * @param {string} identifier - identifier for the external link.
 */
export const trackExternalLink = (track: Track, identifier: string) => {
  track.action(TRACKING_CONTEXT.NAVIGATION_TYPE.LINK, {
    type: TRACKING_CONTEXT.NAVIGATION_TYPE.LINK,
    identifier,
  });
};

/**
 * Tracks a button click or a toggle switch event.
 * @param {Track} track - tracking object.
 * @param {string} pageName - name of the page after the toggle switch/button is clicked.
 * @param {string} customerNumber - customer number.
 * @param {string} messageText - text of the DynamicMessage.
 * @param {string} messageCause - cause of the DynamicMessage.
 * @param {string} navigationType - type of navigation (e.g., toggle switch or button).
 * @param {string} navigationIdentifier - identifier for the navigation.
 */
export const trackButtonOrToggle = (
  track: Track,
  pageName: string,
  customerNumber: string,
  messageText: string,
  messageCause: string,
  navigationType: string,
  navigationIdentifier: string
) => {
  track.view({
    name: `${TRACKING_CONTEXT.PAGE.PREFIX}${pageName}`,
    cis_number: customerNumber,
    authentication_status: FULLY_AUTHENTICATED,
  });

  track.setDynamicMessage({
    cause: messageCause,
    text: messageText,
  });

  track.setNavigation({
    type: navigationType,
    identifier: navigationIdentifier,
  });
};
